import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useOrdersStore } from '../store/ordersStore'
import { useAuthStore } from '../store/authStore'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../components/ui/SafeIcon'

const { FiPackage, FiTruck, FiCheckCircle, FiClock, FiAlertCircle } = FiIcons

function OrdersPage() {
  const { user } = useAuthStore()
  const { orders, getOrdersByUser } = useOrdersStore()
  const userOrders = getOrdersByUser(user?.id)

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return FiCheckCircle
      case 'shipped': return FiTruck
      case 'processing': return FiPackage
      case 'confirmed': return FiClock
      default: return FiAlertCircle
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'text-green-600 bg-green-100'
      case 'shipped': return 'text-blue-600 bg-blue-100'
      case 'processing': return 'text-orange-600 bg-orange-100'
      case 'confirmed': return 'text-yellow-600 bg-yellow-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'delivered': return 'Geliefert'
      case 'shipped': return 'Versandt'
      case 'processing': return 'In Bearbeitung'
      case 'confirmed': return 'Bestätigt'
      default: return status
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">Bitte anmelden</h2>
          <p className="text-gray-600">Sie müssen angemeldet sein, um Bestellungen zu sehen.</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Meine Bestellungen | 1688Euro.com</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Meine Bestellungen</h1>
            <p className="text-gray-600">Verwalten und verfolgen Sie Ihre Bestellungen</p>
          </div>

          {/* Orders List */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Table Header */}
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 hidden md:grid grid-cols-12 gap-4 text-sm font-bold text-gray-600">
              <div className="col-span-3">Bestellnummer</div>
              <div className="col-span-2">Datum</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Artikel</div>
              <div className="col-span-2">Gesamt</div>
              <div className="col-span-1">Aktionen</div>
            </div>

            {/* Orders */}
            {userOrders.length > 0 ? (
              userOrders.map((order) => (
                <div key={order.id} className="border-b border-gray-200 last:border-0 hover:bg-gray-50 transition-colors">
                  <div className="p-6 md:grid md:grid-cols-12 md:gap-4 items-center">
                    {/* Order Number */}
                    <div className="col-span-3 mb-4 md:mb-0">
                      <div className="font-bold text-gray-900">{order.id}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        {order.items.length} Artikel bei {order.items[0]?.supplier?.name}
                      </div>
                    </div>

                    {/* Date */}
                    <div className="col-span-2 mb-4 md:mb-0">
                      <div className="text-sm text-gray-600">
                        {new Date(order.createdAt).toLocaleDateString('de-DE')}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="col-span-2 mb-4 md:mb-0">
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        <SafeIcon icon={getStatusIcon(order.status)} className="w-3 h-3 mr-1" />
                        {getStatusText(order.status)}
                      </div>
                    </div>

                    {/* Items */}
                    <div className="col-span-2 mb-4 md:mb-0">
                      <div className="text-sm text-gray-600">{order.items.length} Artikel</div>
                    </div>

                    {/* Total */}
                    <div className="col-span-2 mb-4 md:mb-0">
                      <div className="font-bold text-gray-900">{order.totals.total.toFixed(2)} €</div>
                    </div>

                    {/* Actions */}
                    <div className="col-span-1">
                      <Link
                        to={`/order/${order.id}`}
                        className="text-blue-600 hover:underline text-sm font-medium"
                      >
                        Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-12 text-center">
                <SafeIcon icon={FiPackage} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Keine Bestellungen gefunden</h3>
                <p className="text-gray-600 mb-4">Sie haben noch keine Bestellungen aufgegeben.</p>
                <Link
                  to="/search?q=alle"
                  className="inline-block bg-blue-600 text-white px-6 py-2 rounded font-bold hover:bg-blue-700"
                >
                  Jetzt einkaufen
                </Link>
              </div>
            )}
          </div>

          {/* Order Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-blue-600 mb-1">{userOrders.length}</div>
              <div className="text-sm text-gray-600">Gesamtbestellungen</div>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-green-600 mb-1">
                {userOrders.filter(o => o.status === 'delivered').length}
              </div>
              <div className="text-sm text-gray-600">Abgeschlossen</div>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-blue-600 mb-1">
                {userOrders.filter(o => o.status === 'shipped').length}
              </div>
              <div className="text-sm text-gray-600">Versandt</div>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-orange-600 mb-1">
                {userOrders.filter(o => o.status === 'processing' || o.status === 'confirmed').length}
              </div>
              <div className="text-sm text-gray-600">In Bearbeitung</div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default OrdersPage